import { useState } from 'react'
import { useKiosk } from '../../hooks/useKiosk'
import { config } from '../../config'
import { layout, ui, cx } from '../../lib/ui-primitives'
import { ScreenAnimator } from '../motion'
import { WelcomeScreen } from './screens/WelcomeScreen'
import { NetworkScreen } from './screens/NetworkScreen'
import { AmountScreen } from './screens/AmountScreen'
import { DetailsScreen } from './screens/DetailsScreen'
import { PaymentScreen } from './screens/PaymentScreen'
import { ConfirmScreen } from './screens/ConfirmScreen'
import { ProcessingScreen } from './screens/ProcessingScreen'
import { LookupScreen } from './screens/LookupScreen'

interface KioskModeProps {
  onAdminAccess: () => void
}

/**
 * KioskMode - Main kiosk container with screen management
 *
 * Enhanced with:
 * - Framer Motion screen transitions (AnimatePresence)
 * - Unified layout primitives
 */
export function KioskMode({ onAdminAccess }: KioskModeProps) {
  const kiosk = useKiosk()
  const [adminPressStart, setAdminPressStart] = useState<number | null>(null)

  // Handle long press for admin access
  const handleLogoPress = () => setAdminPressStart(Date.now())
  const handleLogoRelease = () => {
    if (adminPressStart && Date.now() - adminPressStart > 3000) {
      onAdminAccess()
    }
    setAdminPressStart(null)
  }

  const renderScreen = () => {
    switch (kiosk.screen) {
      case 'welcome':
        return (
          <WelcomeScreen
            kiosk={kiosk}
            onLogoPress={handleLogoPress}
            onLogoRelease={handleLogoRelease}
            onLogoLeave={() => setAdminPressStart(null)}
          />
        )
      case 'network':
        return <NetworkScreen kiosk={kiosk} />
      case 'amount':
        return <AmountScreen kiosk={kiosk} />
      case 'details':
        return <DetailsScreen kiosk={kiosk} />
      case 'payment':
        return <PaymentScreen kiosk={kiosk} />
      case 'confirm':
        return <ConfirmScreen kiosk={kiosk} />
      case 'processing':
        return <ProcessingScreen kiosk={kiosk} />
      case 'lookup':
        return <LookupScreen kiosk={kiosk} />
      default:
        return (
          <WelcomeScreen
            kiosk={kiosk}
            onLogoPress={handleLogoPress}
            onLogoRelease={handleLogoRelease}
            onLogoLeave={() => setAdminPressStart(null)}
          />
        )
    }
  }

  return (
    <div className={cx(layout.backdrop, layout.safeAll)}>
      <div className={layout.kioskShell}>
        {/* Header */}
        <header className={ui.header}>
          <span className={ui.subheaderMono}>{config.businessName}</span>
          <div className="flex items-center gap-2">
            <span
              className="size-2 rounded-full bg-emerald-400 animate-pulse"
              aria-hidden="true"
            />
            <span className="text-white/50 text-xs">Live</span>
          </div>
        </header>

        {/* Content with animated transitions */}
        <main className="flex-1 overflow-hidden">
          <ScreenAnimator currentScreen={kiosk.screen}>
            {renderScreen()}
          </ScreenAnimator>
        </main>
      </div>
    </div>
  )
}
